import React from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseSelector } from '../../redux/hooks'
import { characterConst, converterType } from '../../utils/constant'
import { chineseLang } from '../../i18n/chinese'
import Loader from 'containers/loader/loader'
import { formatPercent } from '../../i18n/format'
import { parseStoryForCard, type StoryTag } from './parseStoryForCard'
import './story-card.css'

export type StoryCardVariant = 'hero' | 'standard' | 'compact'

export interface StoryCardProps {
  story: any
  variant: StoryCardVariant
  onClick?: (story: any) => void
  /** Override tags for display (e.g. pass ['featured'] for the single "today's story" card) */
  displayTags?: StoryTag[]
  /** Show loading overlay (e.g. when navigating to story) */
  loading?: boolean
  /** Control visibility of level pill */
  showLevelBadge?: boolean
}

function towerIcon(num: number) {
  if (num > 33) return <img alt="" src="/images/tower-no-icon.svg" />
  if (num > 20 && num <= 33) return <img alt="" src="/images/tower-one-icon.svg" />
  if (num > 8 && num <= 20) return <img alt="" src="/images/tower-two-icon.svg" />
  return <img alt="" src="/images/tower-fill-icon.svg" />
}

// Literal keys, so the key scan (i18n/keyUsage.ts) sees them.
const TAG_LABEL_KEYS: Record<StoryTag, string> = {
  recommended: 'reader:card.tags.recommended',
  featured: 'reader:card.tags.featured',
  latest: 'reader:card.tags.latest',
}

function tagClass(tag: StoryTag): string {
  switch (tag) {
    case 'recommended':
      return 'story-card-tag story-card-tag--recommended'
    case 'featured':
      return 'story-card-tag story-card-tag--featured'
    case 'latest':
      return 'story-card-tag story-card-tag--latest'
    default:
      return 'story-card-tag'
  }
}

const StoryCard: React.FC<StoryCardProps> = ({
  story,
  variant,
  onClick,
  displayTags,
  loading = false,
  showLevelBadge = true,
}) => {
  const { credentials } = AppUseSelector(({ user }) => user)
  const characterPreference = credentials?.customFields?.character
  const { t } = useTranslation('reader')
  const tagLabel = (tag: StoryTag) => (TAG_LABEL_KEYS[tag] ? t(TAG_LABEL_KEYS[tag]) : tag)
  const wordsKnown = (known: number) => t('card.wordsKnown', { percent: formatPercent(known / 100) })
  const parsed = parseStoryForCard(story)
  const translatedTitle = parsed.translatedTitle
  const tags = displayTags !== undefined ? displayTags : parsed.tags
  const title =
    characterPreference === characterConst.traditional
      ? converterType.converterTraditional(parsed.title)
      : converterType.converterSimplified(parsed.title)

  const handleClick = () => {
    if (loading || !onClick) return
    onClick(story)
  }

  const showImage = variant === 'hero' && parsed.imageUrl
  const showFooter =
    (parsed.knownWords !== null || parsed.newWords !== null) && (variant === 'hero' || variant === 'standard')
  const shouldShowLevelBadge =
    showLevelBadge &&
    parsed.levelLabel &&
    (variant === 'hero' || variant === 'standard' || variant === 'compact')

  const rootClass = [
    'story-card',
    `story-card--${variant}`,
    loading && 'story-card--loading',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={rootClass}
      onClick={variant !== 'compact' ? handleClick : undefined}
      onKeyDown={
        variant !== 'compact'
          ? (e) => e.key === 'Enter' && handleClick()
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {loading && (
        <div className="story-card-loader">
          <Loader />
        </div>
      )}

      {variant === 'hero' && (
        <>
          {parsed.imageUrl && (
            <div className="story-card-media" onClick={handleClick}>
              <img
                className={parsed.isRead ? 'story-card-img story-card-img--read' : 'story-card-img'}
                alt=""
                src={parsed.imageUrl}
              />
            </div>
          )}
          <div className="story-card-body">
            <div onClick={handleClick}>
              {tags.map((tag) =>
                tag === 'recommended' ? (
                  <div key={tag} className="story-card-tag-tooltip">
                    <span className={tagClass(tag)}>{tagLabel(tag)}</span>
                    <div className="story-card-tooltip-content story-card-tooltip-content--tag">
                      <p className="story-card-tooltip-text">{t('card.recommendedTooltip')}</p>
                    </div>
                  </div>
                ) : tag === 'featured' ? (
                  <div key={tag} className="story-card-tag-tooltip">
                    <span className={tagClass(tag)}>{tagLabel(tag)}</span>
                    <div className="story-card-tooltip-content story-card-tooltip-content--tag">
                      <p className="story-card-tooltip-text">{t('card.featuredTooltip')}</p>
                    </div>
                  </div>
                ) : (
                  <span key={tag} className={tagClass(tag)}>
                    {tagLabel(tag)}
                  </span>
                )
              )}
              <h4 className="story-card-title" lang={chineseLang(characterPreference)}>{title}</h4>
              <p className="story-card-english" lang={translatedTitle.lang}>
                {translatedTitle.text}
              </p>
            </div>
            {showFooter && (
              <div className="story-card-footer">
                <div className="story-card-creator-tooltip">
                  <img alt="" src="/images/maayot-green-logo-small.svg" />
                  <div className="story-card-tooltip-content">
                    <p className="story-card-tooltip-text">{t('card.teacherTooltip')}</p>
                  </div>
                </div>
                {(parsed.knownWords !== null || parsed.newWords !== null) && (
                  <div className="story-card-footer-divider" />
                )}
                {parsed.knownWords !== null && (
                  <div className="story-card-meta-tooltip">
                    <div className="story-card-meta-row">
                      {towerIcon(parsed.diff)}
                      <label>{wordsKnown(parsed.knownWords)}</label>
                      <div className="story-card-tooltip-content">
                        <p className="story-card-tooltip-text">{t('card.wordsKnownTooltip')}</p>
                      </div>
                    </div>
                  </div>
                )}
                {parsed.newWords !== null && (
                  <div className="story-card-meta-tooltip story-card-meta-tooltip--new">
                    <div className="story-card-meta-row">
                      <img alt="" src="/images/sparkles-icon.svg" />
                      <label>{t('card.newWords', { count: parsed.newWords })}</label>
                      <div className="story-card-tooltip-content">
                        <p className="story-card-tooltip-text">{t('card.newWordsTooltip')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {parsed.isRead && (
              <div className="story-card-check">
                <img alt="" src="/images/check-icon.svg" />
              </div>
            )}
          </div>
        </>
      )}

      {variant === 'standard' && (
        <>
          {parsed.imageUrl && showImage && (
            <div className="story-card-media story-card-media--standard" onClick={handleClick}>
              <img
                className={parsed.isRead ? 'story-card-img story-card-img--read' : 'story-card-img'}
                alt=""
                src={parsed.imageUrl}
              />
            </div>
          )}
          <div className="story-card-body story-card-body--standard">
            <div onClick={handleClick}>
              {tags.map((tag) =>
                tag === 'recommended' ? (
                  <div key={tag} className="story-card-tag-tooltip">
                    <span className={tagClass(tag)}>{tagLabel(tag)}</span>
                    <div className="story-card-tooltip-content story-card-tooltip-content--tag">
                      <p className="story-card-tooltip-text">{t('card.recommendedTooltip')}</p>
                    </div>
                  </div>
                ) : tag === 'featured' ? (
                  <div key={tag} className="story-card-tag-tooltip">
                    <span className={tagClass(tag)}>{tagLabel(tag)}</span>
                    <div className="story-card-tooltip-content story-card-tooltip-content--tag">
                      <p className="story-card-tooltip-text">{t('card.featuredTooltip')}</p>
                    </div>
                  </div>
                ) : (
                  <span key={tag} className={tagClass(tag)}>
                    {tagLabel(tag)}
                  </span>
                )
              )}
              <div className="story-card-title-row">
                <h4 className="story-card-title" lang={chineseLang(characterPreference)}>{title}</h4>
                <p className="story-card-english" lang={translatedTitle.lang}>
                {translatedTitle.text}
              </p>
              </div>
            </div>
            {showFooter && (
              <div className="story-card-footer">
                {parsed.knownWords !== null && (
                  <div className="story-card-meta-tooltip">
                    <div className="story-card-meta-row">
                      {towerIcon(parsed.diff)}
                      <label>{wordsKnown(parsed.knownWords)}</label>
                      <div className="story-card-tooltip-content">
                        <p className="story-card-tooltip-text">{t('card.wordsKnownTooltip')}</p>
                      </div>
                    </div>
                  </div>
                )}
                {parsed.newWords !== null && (
                  <div className="story-card-meta-tooltip story-card-meta-tooltip--new">
                    <div className="story-card-meta-row">
                      <img alt="" src="/images/sparkles-icon.svg" />
                      <label>{t('card.newWords', { count: parsed.newWords })}</label>
                      <div className="story-card-tooltip-content">
                        <p className="story-card-tooltip-text">{t('card.newWordsTooltip')}</p>
                      </div>
                    </div>
                  </div>
                )}
                {shouldShowLevelBadge && parsed.levelLabel && (
                  <span className="story-card-level-badge">{parsed.levelLabel}</span>
                )}
              </div>
            )}
            {parsed.isRead && (
              <div className="story-card-check">
                <img alt="" src="/images/check-icon.svg" />
              </div>
            )}
          </div>
        </>
      )}

      {variant === 'compact' && (
        <>
          <div
            className="story-card-body story-card-body--compact"
            onClick={handleClick}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            role="button"
            tabIndex={0}
          >
            {tags.length > 0 && (
              <div className="story-card-tags-row">
                {tags.map((tag) =>
                  tag === 'featured' ? (
                    <div key={tag} className="story-card-tag-tooltip">
                      <span className={tagClass(tag)}>{tagLabel(tag)}</span>
                      <div className="story-card-tooltip-content story-card-tooltip-content--tag">
                        <p className="story-card-tooltip-text">{t('card.featuredTooltip')}</p>
                      </div>
                    </div>
                  ) : (
                    <span key={tag} className={tagClass(tag)}>
                      {tagLabel(tag)}
                    </span>
                  )
                )}
              </div>
            )}
            <div className="story-card-title-row">
              <h5 className="story-card-title story-card-title--compact" lang={chineseLang(characterPreference)}>{title}</h5>
              <p className="story-card-english story-card-english--compact" lang={translatedTitle.lang}>
                {translatedTitle.text}
              </p>
            </div>
            <div className="story-card-meta story-card-meta--compact">
              {parsed.knownWords !== null && (
                <div className="story-card-meta-item">
                  {towerIcon(parsed.diff)}
                  <span>{wordsKnown(parsed.knownWords)}</span>
                </div>
              )}
              {parsed.newWords !== null && (
                <div className="story-card-meta-item">
                  <img alt="" src="/images/sparkles-icon.svg" />
                  <span>{t('card.newWords', { count: parsed.newWords })}</span>
                </div>
              )}
              {shouldShowLevelBadge && parsed.levelLabel && (
                <span className="story-card-level-badge">{parsed.levelLabel}</span>
              )}
            </div>
          </div>
          {parsed.isRead && (
            <div className="story-card-check story-card-check--compact">
              <img alt="" src="/images/check-icon.svg" />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default StoryCard
